/**
 * Small chat-UI helpers shared across the client.
 *
 * - `resolveChatIcon`: validates the AI-picked lucide icon name and falls
 *   back to a default when the model picks an unknown one.
 * - `getChatIdFromPathname`: reads the chat id directly from
 *   `window.location.pathname` for cases where React Router's `useParams`
 *   lags (e.g. the workspace ChatPage updates the URL via
 *   `history.replaceState` to keep `<Chat>` mounted, which RR doesn't
 *   observe).
 */

import { iconNames } from "lucide-react/dynamic";

/** Default icon used when the AI picks an unknown name or no icon is set. */
export const DEFAULT_CHAT_ICON = "message-circle" as const;

/** Pre-built Set for O(1) name lookups. */
const VALID_ICON_NAMES: Set<string> = new Set(iconNames);

/**
 * Returns the icon name if it's a valid lucide icon, otherwise the default.
 * Accepts undefined / null / unknown strings gracefully so callers don't have
 * to guard each access (e.g. old chats without `metadata.icon`).
 */
export function resolveChatIcon(name: string | null | undefined): string {
  if (!name) return DEFAULT_CHAT_ICON;
  return VALID_ICON_NAMES.has(name) ? name : DEFAULT_CHAT_ICON;
}

/**
 * Extracts the chat id from the current URL pathname.
 *
 * Both the workspace ChatPage and AgentChatDetailsPage replace the URL via
 * `window.history.replaceState` after the first stream so the live `<Chat>`
 * instance stays mounted (preserves useChat state, scroll position, title
 * typing animation). React Router does not observe `replaceState`, so
 * `useParams()` stays stuck on the pre-stream match — `undefined` for the
 * workspace flow and the literal string `"new"` for the agent flow — until
 * the next React-Router navigation. Pathname is the live source of truth in
 * both cases, so components that need the chat id (e.g. the file preview
 * dialog opening right after stream completion) should read it from here.
 */
export function getChatIdFromPathname(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const match = window.location.pathname.match(/\/chat\/([^/?#]+)/);
  if (!match) return undefined;
  // Guard against the literal "new" sentinel (agent new-chat route) in case
  // a caller reads pathname before replaceState fires.
  return match[1] === "new" ? undefined : match[1];
}
